import React, { useRef } from 'react';
import EDTreeSelectListItem from './EDTreeSelectListItem';
import EDCollapse from '../Collapsables/Collapse/EDCollapse';
import {
  searchValueFilter,
  ICheckedItems,
  ITreeSection,
  SectionCheckStatus,
} from './TreeSelectUtils';
import EDTreeSelectSection from './EDTreeSelectSection';
import { useNonInitialEffect } from '../../Hooks/useNonInitialEffect';
import debounce from 'debounce';
import { makeStyles } from 'tss-react/mui';
import { ArboristTreeContext } from '../../Components/Urval/Components/UrvalFieldPicker/Components/ArboristTreeNodeContext';
import { AutoSizer } from 'react-virtualized';
import EDVirtualTreeSelectListItem from './EDVirtualTreeSelectListItem';
import { Tree } from 'react-arborist';
import EDGrid from '../GridComponents/EDGrid';

export interface ITreeSectionProps<T> {
  sections: ITreeSection<T>[];
  searchValue: string;
  sectionsUpdate: (sections: ITreeSection<T>[]) => void;
  isChecked?: (section: ITreeSection<T>) => SectionCheckStatus;
  /*To avoid setting the state multiple times with potential errors
    the finalised sections have to be returned to let TreeSelect update
    the state. If no changes are made to sections, simply return the
    sections parameter.*/
  onCheck?: (
    sections: ITreeSection<T>[],
    checkedSection: ICheckedItems,
    checked: boolean,
    suggestion?: ITreeSection<T>
  ) => ITreeSection<T>[];
  disableSectionsClick?: boolean | ((section: ITreeSection<T>) => boolean);
  showListItemButtons?: (section: ITreeSection<T>) => boolean;
  renderListItemButtons?: (
    section: ITreeSection<T>,
    parent?: ITreeSection<T>
  ) => React.ReactNode;
  hoverChecklistButtons?: (analys: ITreeSection<T>) => boolean;
  renderSecondaryText?: (suggestion: ITreeSection<T>) => React.ReactNode;
  searchFilterFunction?: (
    sections: ITreeSection<T>[],
    searchValue: string
  ) => ITreeSection<T>[];
  isInitialStateOpen?: boolean | ((suggestion: ITreeSection<T>) => boolean);
  parentSectionBoldText?: boolean;
  showSubSectionLines?: boolean | number;
  // Horizontal lines that extend from the vertical section line. Appears on level >= showPointerLines value.
  showPointerLines?: number;
  virtual?: boolean;
  filteredSections?: ITreeSection<T>[];
  /** Renders the tree in a grid if true */
  grid?: boolean;
}

export interface ITreeSelectProps<T> extends ITreeSectionProps<T> {
  containerRef?: React.RefObject<HTMLDivElement | null>;
  sortMode?: string;
  renderSectionHeader?: (section: ITreeSection<T>) => React.ReactNode;
}

const useStyles = makeStyles()(theme => ({
  container: {
    paddingLeft: theme.spacing(2),
    borderLeft: `2px solid #d7d7d7`,
  },
}));

function EDTreeSelect<T = any>(props: ITreeSelectProps<T>) {
  const { containerRef } = props;
  const { classes } = useStyles();
  const searchFilter = useRef(
    debounce(
      (
        items: ITreeSection<T>[],
        searchValue: string,
        update: (sections: ITreeSection<T>[]) => void,
        searchFunction?: (
          sections: ITreeSection<T>[],
          searchValue: string
        ) => ITreeSection<T>[]
      ) => {
        if (searchFunction) {
          update(searchFunction(items, searchValue));
        } else {
          const { sections } = searchValueFilter(
            items,
            searchValue,
            props.isInitialStateOpen
          );
          update(sections);
        }
      },
      500
    )
  ).current;
  const arboristTreeRef = useRef(null);

  useNonInitialEffect(() => {
    searchFilter(
      props.sections,
      props.searchValue,
      props.sectionsUpdate,
      props.searchFilterFunction
    );
  }, [props.searchValue]);

  const renderTreeSelectSection = (
    section: ITreeSection<T>,
    sectionIndex: number
  ): React.JSX.Element => (
    <>
      <EDTreeSelectSection
        sections={props.sections}
        searchValue={props.searchValue}
        section={section}
        sectionIndex={sectionIndex}
        sectionsUpdate={props.sectionsUpdate}
        onCheck={props.onCheck}
        disableSectionsClick={props.disableSectionsClick}
        hoverChecklistButtons={props.hoverChecklistButtons}
        renderListItemButtons={props.renderListItemButtons}
        showListItemButtons={props.showListItemButtons}
        renderSecondaryText={props.renderSecondaryText}
        parentSectionBoldText={props.parentSectionBoldText}
      />
      <EDCollapse unmountOnExit mountOnEnter open={Boolean(section.open)}>
        <div className={classes.container}>
          <EDTreeSelectListItem
            {...props}
            section={section}
            selections={section.subList ?? []}
            sectionIndex={sectionIndex}
            level={1}
          />
        </div>
      </EDCollapse>
    </>
  );

  const renderTreeSections = (): (React.JSX.Element | null)[] => {
    return props.sections.map((section, sectionIndex) => {
      let backgroundColor = '#FFFFFF';

      if (sectionIndex % 2 !== 0) {
        backgroundColor = '#F8F8F8';
      }

      if (section.hidden) {
        return null;
      } else if (props.grid) {
        return (
          <EDGrid
            style={{
              backgroundColor: backgroundColor,
            }}
            item
            xs={12}
            md={4}
            key={sectionIndex}
          >
            {renderTreeSelectSection(section, sectionIndex)}
          </EDGrid>
        );
      } else {
        return (
          <div key={sectionIndex}>
            {renderTreeSelectSection(section, sectionIndex)}
          </div>
        );
      }
    });
  };

  if (!props.virtual) {
    if (props.grid) {
      return (
        <EDGrid ref={containerRef} container>
          {renderTreeSections()}
        </EDGrid>
      );
    } else {
      return <div ref={containerRef}>{renderTreeSections()}</div>;
    }
  } else {
    return (
      <div ref={arboristTreeRef} style={{ height: '100%' }}>
        <ArboristTreeContext.Provider
          value={{
            onCheck: props.onCheck,
            searchValue: props.searchValue,
            renderListItemButtons: props.renderListItemButtons,
            showListItemButtons: props.showListItemButtons,
            sectionsUpdate: props.sectionsUpdate,
            disableSectionsClick: props.disableSectionsClick,
            sections: props.sections,
          }}
        >
          <AutoSizer>
            {autoSizerProps =>
              arboristTreeRef.current ? (
                <Tree<ITreeSection<T>>
                  data={{
                    id: 'root',
                    name: 'Root',
                    value: 'root',
                    subList: props.filteredSections,
                  }}
                  getChildren="subList"
                  isOpen="open"
                  hideRoot
                  rowHeight={42}
                  width={autoSizerProps.width}
                  height={autoSizerProps.height}
                  dndRootElement={arboristTreeRef.current}
                  disableDrag
                  disableDrop
                >
                  {EDVirtualTreeSelectListItem}
                </Tree>
              ) : null
            }
          </AutoSizer>
        </ArboristTreeContext.Provider>
      </div>
    );
  }
}

export default EDTreeSelect;
